import { useState, useCallback, useEffect, useRef } from 'react';

import i18n from '../lib/i18n';

interface AsyncState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

interface UseAsyncOptions {
  immediate?: boolean;
  onSuccess?: (data: unknown) => void;
  onError?: (error: string) => void;
}

function getErrorMessage(error: unknown): string {
  // Handle Axios errors
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as {
      response?: {
        data?: { message?: string; error?: string };
        status?: number;
      };
      message?: string;
      code?: string;
    };

    // Network error
    if (axiosError.code === 'ERR_NETWORK') {
      return i18n.t('errors:network.noConnection');
    }

    // Timeout
    if (axiosError.code === 'ECONNABORTED') {
      return i18n.t('errors:network.timeout');
    }

    // Server response error
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }

    // HTTP status code based errors
    if (axiosError.response?.status) {
      const status = axiosError.response.status;
      if (status === 401) return i18n.t('errors:general.unauthorized');
      if (status === 403) return i18n.t('errors:general.forbidden');
      if (status === 404) return i18n.t('errors:general.pageNotFound');
      if (status >= 500) return i18n.t('errors:general.serverError');
    }

    // Generic error message
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return i18n.t('errors:general.somethingWentWrong');
}

export function useAsync<T>(
  asyncFunction: () => Promise<T>,
  options: UseAsyncOptions = {}
) {
  const { immediate = false, onSuccess, onError } = options;
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    isLoading: immediate,
    error: null,
  });

  const mountedRef = useRef(true);
  // Store latest refs to avoid re-creating execute on every render
  const asyncFunctionRef = useRef(asyncFunction);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Keep refs current without triggering re-renders
  asyncFunctionRef.current = asyncFunction;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const execute = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await asyncFunctionRef.current();
      if (mountedRef.current) {
        setState({ data: result, isLoading: false, error: null });
        onSuccessRef.current?.(result);
      }
      return result;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      if (mountedRef.current) {
        setState({ data: null, isLoading: false, error: errorMessage });
        onErrorRef.current?.(errorMessage);
      }
      throw err;
    }
  }, []); // Stable reference - uses refs internally

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (immediate) {
      execute().catch(() => {
        // Error is already handled in state
      });
    }
    return () => {
      mountedRef.current = false;
    };
  }, [immediate, execute]);

  return {
    ...state,
    execute,
    reset,
    isSuccess: !state.isLoading && !state.error && state.data !== null,
    isError: !state.isLoading && state.error !== null,
  };
}

export function useAsyncCallback<T, Args extends unknown[]>(
  asyncFunction: (...args: Args) => Promise<T>,
  options: UseAsyncOptions = {}
) {
  const { onSuccess, onError } = options;
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const mountedRef = useRef(true);
  // Store latest refs to avoid re-creating execute on every render
  const asyncFunctionRef = useRef(asyncFunction);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  asyncFunctionRef.current = asyncFunction;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const execute = useCallback(
    async (...args: Args) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await asyncFunctionRef.current(...args);
        if (mountedRef.current) {
          setState({ data: result, isLoading: false, error: null });
          onSuccessRef.current?.(result);
        }
        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        if (mountedRef.current) {
          setState({ data: null, isLoading: false, error: errorMessage });
          onErrorRef.current?.(errorMessage);
        }
        throw err;
      }
    },
    [] // Stable reference - uses refs internally
  );

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    ...state,
    execute,
    reset,
    isSuccess: !state.isLoading && !state.error && state.data !== null,
    isError: !state.isLoading && state.error !== null,
  };
}

// Hook for handling online/offline status
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
